import { v4 as uuidv4 } from 'uuid';

import {
  getAllCompositeSegments,
  getClipBroadcastId,
  getClipFPS,
  getCompositeSegmentById
} from '@goldcast/player-config/src/utils/clipUtils';

import { LayoutDisableReason, LayoutType, FitType } from '@goldcast/player-config/src/types/asset-types';
import { getVideosAndTimeRanges } from '@goldcast/player-config/src/utils/videoRangeUtils';
import { Clip } from '@goldcast/player-config/src/types/clip-types';
import { Scene } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Scene';
import { ORIGINAL_VIDEO_ID } from '@goldcast/player-config/src/constants/screenshare';
import { getSpeakerNameAndTitle } from '@goldcast/player-config/src/utils/speakerUtils';
import { isLayoutAudiogram, isLayoutDefault, isSpeakerLayout } from '@goldcast/player-config/src/utils/layout';
import {
  selectSpeakerDetails,
  selectVideoFeeds
} from '@goldcast/player-config/src/selectors/contentMetadataStoreSelectors';
import { VideoFeedType } from '@goldcast/player-config/src/types/video-feed-types';
import {
  isFullVideoFaceRecEnabled,
  isFullVideoFaceRecForPreRecordedEnabled,
  isLayoutsWithVisionDataEnabled
} from '@/stores/clip';
import { useContentMetadataStore } from '@/stores/contentMetaDataStore/contentMetaDataStore';
import { getSpeakerKeyFromVideoFeed, getVideoFeedById } from '@/utils/videoFeeds';
import {
  buildSceneTracks,
  SCREENSHARE_TRACK_NAME,
  SHARED_VIDEO_TRACK_NAME
} from '@/stores/contentMetaDataStore/utils/sceneTracks';

export const createNewScene = ({
  newClip,
  currentFrame,
  compositeId
}: {
  newClip: Clip;
  currentFrame: number;
  compositeId: string;
}) => {
  const compositeSegment = getCompositeSegmentById(newClip, compositeId);
  const layout = newClip.asset_metadata.layout as LayoutType;
  const fitMode = newClip.asset_metadata.fit as FitType;
  const all_video_ids = getVideosAndTimeRanges({
    clip: newClip,
    isFullFaceRecEnabled: isFullVideoFaceRecEnabled(),
    isFullFaceRecForPreRecordedEnabled: isFullVideoFaceRecForPreRecordedEnabled(),
    contentMetadataStoreInstance: useContentMetadataStore
  }).map(video => video.videoId);

  return [
    {
      id: uuidv4(),
      name: 'Scene 1',
      fromFrame: compositeSegment.currentBounds.fromFrame,
      toFrame: currentFrame,
      details: {
        layout,
        fitMode,
        visible_video_ids: all_video_ids,
        all_video_ids: all_video_ids
      }
    },
    {
      id: uuidv4(),
      name: 'Scene 2',
      fromFrame: currentFrame,
      toFrame: compositeSegment.currentBounds.toFrame,
      details: {
        layout,
        fitMode,
        visible_video_ids: all_video_ids,
        all_video_ids: all_video_ids
      }
    }
  ];
};

export const createNewSceneAtRange = ({
  newClip,
  startTimeFrame,
  endTimeFrame,
  compositeId
}: {
  newClip: Clip;
  startTimeFrame: number;
  endTimeFrame: number;
  compositeId: string;
}) => {
  const compositeSegment = getCompositeSegmentById(newClip, compositeId);

  const scenes = compositeSegment.scenes || [];

  const layout = newClip.asset_metadata.layout as LayoutType;
  const fitMode = newClip.asset_metadata.fit as FitType;
  const all_video_ids = getVideosAndTimeRanges({
    clip: newClip,
    isFullFaceRecEnabled: isFullVideoFaceRecEnabled(),
    isFullFaceRecForPreRecordedEnabled: isFullVideoFaceRecForPreRecordedEnabled(),
    contentMetadataStoreInstance: useContentMetadataStore
  }).map(video => video.videoId);
  const visible_video_ids =
    scenes.find(scene => scene.fromFrame <= startTimeFrame && scene.toFrame >= startTimeFrame)?.details
      .visible_video_ids || all_video_ids;

  return [
    {
      id: uuidv4(),
      name: 'Scene 1',
      fromFrame: compositeSegment.currentBounds.fromFrame,
      toFrame: startTimeFrame,
      details: {
        layout,
        fitMode,
        visible_video_ids: visible_video_ids,
        all_video_ids: all_video_ids
      }
    },
    {
      id: uuidv4(),
      name: 'Scene 2',
      fromFrame: startTimeFrame,
      toFrame: endTimeFrame,
      details: {
        layout,
        fitMode,
        visible_video_ids: visible_video_ids,
        all_video_ids: all_video_ids
      }
    },
    {
      id: uuidv4(),
      name: 'Scene 3',
      fromFrame: endTimeFrame,
      toFrame: compositeSegment.currentBounds.toFrame,
      details: {
        layout,
        fitMode,
        visible_video_ids: visible_video_ids,
        all_video_ids: all_video_ids
      }
    }
  ];
};

export const addNewSceneAtFrame = ({
  newClip,
  currentFrame,
  compositeId
}: {
  newClip: Clip;
  currentFrame: number;
  compositeId: string;
}) => {
  const compositeSegment = getCompositeSegmentById(newClip, compositeId);
  if (!compositeSegment.segment_metadata) {
    throw new Error('Composite segment metadata not found');
  }
  const scenes = compositeSegment.segment_metadata.scenes || [];
  const currentSceneIndex = scenes.findIndex(scene => scene.fromFrame <= currentFrame && scene.toFrame >= currentFrame);

  // If no scene is found, return the original scenes array
  if (currentSceneIndex === -1) {
    return scenes;
  }

  const currentScene = scenes[currentSceneIndex];
  const nextScene = scenes[currentSceneIndex + 1];

  return [
    ...scenes.slice(0, currentSceneIndex),
    { ...currentScene, toFrame: currentFrame },
    {
      id: uuidv4(),
      name: `Scene ${Math.round(Math.random() * 100)}`,
      fromFrame: currentFrame,
      toFrame: nextScene ? nextScene.fromFrame : compositeSegment.currentBounds.toFrame,
      details: {
        ...currentScene.details
      }
    },
    ...scenes.slice(currentSceneIndex + 1)
  ];
};

export const addNewSceneAtRange = ({
  newClip,
  startTimeFrame,
  endTimeFrame,
  compositeId
}: {
  newClip: Clip;
  startTimeFrame: number;
  endTimeFrame: number;
  compositeId: string;
}) => {
  const compositeSegment = getCompositeSegmentById(newClip, compositeId);
  const scenes = compositeSegment.scenes || [];
  // Find scenes that overlap with the range
  const scenesToRemove: number[] = [];

  scenes.forEach((scene, index) => {
    // Check if scene completely overlaps with the range
    const completeSceneOverlaps = scene.toFrame <= endTimeFrame && scene.fromFrame >= startTimeFrame;

    if (completeSceneOverlaps) {
      scenesToRemove.push(index);
    }
  });

  // Start with all scenes that don't overlap
  const updatedScenes = scenes
    .filter((_, index) => !scenesToRemove.includes(index))
    .map(scene => {
      if (scene.fromFrame <= startTimeFrame && scene.toFrame >= startTimeFrame) {
        return {
          ...scene,
          toFrame: startTimeFrame
        };
      }
      if (scene.toFrame >= endTimeFrame && scene.fromFrame <= endTimeFrame) {
        return {
          ...scene,
          fromFrame: endTimeFrame
        };
      }
      return scene;
    });

  // Check if there's already a scene at endFrame
  const existingSceneAtEnd = updatedScenes.find(scene => scene.fromFrame === endTimeFrame);
  const nextSceneAfterEnd = updatedScenes.find(scene => scene.fromFrame > endTimeFrame);
  const layout = newClip.asset_metadata.layout as LayoutType;
  const fitMode = newClip.asset_metadata.fit as FitType;
  const all_video_ids = getVideosAndTimeRanges({
    clip: newClip,
    isFullFaceRecEnabled: isFullVideoFaceRecEnabled(),
    isFullFaceRecForPreRecordedEnabled: isFullVideoFaceRecForPreRecordedEnabled(),
    contentMetadataStoreInstance: useContentMetadataStore
  }).map(video => video.videoId);

  if (!existingSceneAtEnd) {
    // Create a new scene at endFrame
    updatedScenes.push({
      id: uuidv4(),
      name: `Scene ${Math.round(Math.random() * 100)}`,
      fromFrame: endTimeFrame,
      toFrame: nextSceneAfterEnd?.fromFrame || compositeSegment.currentBounds.toFrame,
      details: {
        layout,
        fitMode,
        visible_video_ids: all_video_ids,
        all_video_ids: all_video_ids
      }
    });
  }

  // Create a new scene at startFrame
  updatedScenes.push({
    id: uuidv4(),
    name: `Scene ${Math.round(Math.random() * 100)}`,
    fromFrame: startTimeFrame,
    toFrame: endTimeFrame,
    details: {
      layout,
      fitMode,
      visible_video_ids: all_video_ids,
      all_video_ids: all_video_ids
    }
  });

  // Sort scenes by fromFrame to maintain order
  updatedScenes.sort((a, b) => a.fromFrame - b.fromFrame);
  return updatedScenes;
};

export const deleteScene = ({
  newClip,
  compositeId,
  sceneId
}: {
  newClip: Clip;
  compositeId: string;
  sceneId: string;
}) => {
  const compositeSegment = getCompositeSegmentById(newClip, compositeId);
  if (!compositeSegment.segment_metadata) {
    throw new Error('Composite segment metadata not found');
  }
  const scenes = compositeSegment.segment_metadata.scenes || [];
  const sceneIndex = scenes.findIndex(scene => scene.id === sceneId);

  if (sceneIndex === -1) {
    throw new Error('Scene not found');
  }

  let updatedScenes: Scene[] = [];

  if (scenes.length === 1) {
    // If this is the last scene, just remove it
    updatedScenes = [];
  } else if (sceneIndex === 0) {
    // If deleting the first scene, merge with the next scene
    const nextScene = scenes[1];
    updatedScenes = [
      {
        ...nextScene,
        fromFrame: scenes[0].fromFrame
      },
      ...scenes.slice(2)
    ];
  } else if (sceneIndex === scenes.length - 1) {
    // If deleting the last scene, merge with the previous scene
    const prevScene = scenes[sceneIndex - 1];
    updatedScenes = [
      ...scenes.slice(0, sceneIndex - 1),
      {
        ...prevScene,
        toFrame: scenes[sceneIndex].toFrame
      }
    ];
  } else {
    // If deleting a middle scene, merge with the next scene
    const prevScene = scenes[sceneIndex - 1];
    const nextScene = scenes[sceneIndex + 1];
    updatedScenes = [
      ...scenes.slice(0, sceneIndex - 1),
      {
        ...prevScene,
        toFrame: nextScene.fromFrame
      },
      ...scenes.slice(sceneIndex + 1)
    ];
  }
  return updatedScenes;
};

export const toggleVideoVisibilityFromScene = ({
  newClip,
  compositeId,
  trackName,
  sceneId,
  videoId
}: {
  newClip: Clip;
  compositeId: string;
  trackName?: string;
  videoId?: string;
  sceneId?: string;
}) => {
  if (!(trackName || videoId)) {
    throw new Error('Track ID or video ID is required');
  }

  const compositeSegment = getCompositeSegmentById(newClip, compositeId);
  if (!compositeSegment.segment_metadata) {
    throw new Error('Composite segment metadata not found');
  }
  const scenes = compositeSegment.segment_metadata.scenes || [];
  if (!sceneId) {
    return scenes;
  }
  const sceneIndex = scenes.findIndex(scene => scene.id === sceneId);

  if (sceneIndex === -1) {
    throw new Error('Scene not found');
  }
  const broadcastId = getClipBroadcastId(newClip)!;
  const state = useContentMetadataStore.getState();
  const speakerDetails = selectSpeakerDetails(broadcastId)(state);
  const videoFeeds = selectVideoFeeds(broadcastId)(state);
  const allVideoIds = scenes[sceneIndex].details.all_video_ids;
  const visibleVideoIds = scenes[sceneIndex].details.visible_video_ids;
  const tracks = buildSceneTracks({
    scene: {
      ...scenes[sceneIndex],
      details: {
        ...scenes[sceneIndex].details,
        all_video_ids: allVideoIds,
        visible_video_ids: visibleVideoIds
      }
    },
    videoFeeds,
    speakerDetails
  });
  const tracksWithFallback =
    tracks.length > 0 && videoFeeds.length > 0
      ? tracks
      : allVideoIds.map(id => {
          const speakerDetailsById = speakerDetails;
          const videoFeed = getVideoFeedById(videoFeeds, id);
          const speakerKey = getSpeakerKeyFromVideoFeed(videoFeed) || null;
          const isUserRecording = videoFeed?.type === VideoFeedType.USER_FEED;
          const isSharedVideo = videoFeed?.type === VideoFeedType.SHARED_VIDEO;
          const speaker = speakerDetailsById[speakerKey ?? ''];

          let name = SCREENSHARE_TRACK_NAME;
          if (isUserRecording && speaker) {
            name = getSpeakerNameAndTitle(speaker).name;
          } else if (id === ORIGINAL_VIDEO_ID) {
            name = 'Original track';
          } else if (isSharedVideo) {
            name = SHARED_VIDEO_TRACK_NAME;
          }

          return {
            id,
            name,
            toggleKey: speakerKey ?? name,
            selected: visibleVideoIds.includes(id),
            bgColor: 'bg-slate-100',
            lightBgColor: 'bg-slate-100',
            color: 'text-slate-700'
          };
        });

  const newVisibleVideoIds: string[] = [];

  const toggledTrack = tracksWithFallback.find(
    track =>
      track.id === videoId || (trackName !== undefined && (track.toggleKey === trackName || track.name === trackName))
  );

  if (!toggledTrack) {
    throw new Error('Track not found');
  }

  const isScreenshareTrackToggled = toggledTrack.name === SCREENSHARE_TRACK_NAME;
  const isSharedVideoTrackToggled = toggledTrack.name === SHARED_VIDEO_TRACK_NAME;

  const anyScreenShareIsSelected = tracksWithFallback.find(
    track => track.name === SCREENSHARE_TRACK_NAME && track.selected
  );
  const anySharedVideoIsSelected = tracksWithFallback.find(
    track => track.name === SHARED_VIDEO_TRACK_NAME && track.selected
  );

  tracksWithFallback.forEach(track => {
    if (track.name === SCREENSHARE_TRACK_NAME && isScreenshareTrackToggled) {
      if (!anyScreenShareIsSelected) {
        newVisibleVideoIds.push(track.id);
      }
      return;
    }

    if (track.name === SHARED_VIDEO_TRACK_NAME && isSharedVideoTrackToggled) {
      if (!anySharedVideoIsSelected) {
        newVisibleVideoIds.push(track.id);
      }
      return;
    }

    if (track.id === toggledTrack.id ? !track.selected : track.selected) {
      newVisibleVideoIds.push(track.id);
    }
  });

  scenes[sceneIndex].details.visible_video_ids = newVisibleVideoIds;
  return scenes;
};

export const getFitFillDisabledReasonForScene = ({ clip, scene }: { scene: Scene; clip: Clip }): string | null => {
  if (isLayoutAudiogram(scene.details.layout)) {
    return LayoutDisableReason.FIT_FILL_NOT_SUPPORTED_FOR_AUDIOGRAM;
  }
  if (isLayoutDefault(scene.details.layout)) {
    return LayoutDisableReason.FILL_NOT_SUPPORTED_FOR_ORIGINAL_LAYOUT;
  }
  if (isSpeakerLayout(scene.details.layout) && isLayoutsWithVisionDataEnabled(clip)) {
    return LayoutDisableReason.FIT_NOT_SUPPORTED_FOR_UPLOADS_WITH_SPEAKER_LAYOUT;
  }
  return null;
};

export const isFitAppliedForScene = ({ clip, scene }: { scene: Scene; clip: Clip }): boolean => {
  const disableReason = getFitFillDisabledReasonForScene({ clip, scene });
  if (disableReason === LayoutDisableReason.FILL_NOT_SUPPORTED_FOR_ORIGINAL_LAYOUT) {
    return true;
  }
  if (disableReason === LayoutDisableReason.FIT_NOT_SUPPORTED_FOR_UPLOADS_WITH_SPEAKER_LAYOUT) {
    return false;
  }
  return scene.details.fitMode === 'FIT';
};

export const trimScenes = (newClip: Clip) => {
  const allCompositeSegments = getAllCompositeSegments(newClip);
  allCompositeSegments.forEach(compositeSegment => {
    if (!compositeSegment.segment_metadata) {
      throw new Error('Composite segment metadata not found');
    }

    const { start, end, scenes } = compositeSegment.segment_metadata;
    const fps = getClipFPS(newClip);

    if (scenes.length === 0) {
      return;
    }

    const fromFrame = start * fps;
    const toFrame = end * fps;

    const updatedScenes = scenes
      .map(scene => {
        return {
          ...scene,
          fromFrame: Math.min(Math.max(scene.fromFrame, fromFrame), toFrame),
          toFrame: Math.min(Math.max(scene.toFrame, fromFrame), toFrame)
        };
      })
      .filter(scene => scene.fromFrame !== scene.toFrame);

    updatedScenes[0].fromFrame = fromFrame;
    updatedScenes[updatedScenes.length - 1].toFrame = toFrame;

    compositeSegment.segment_metadata.scenes = updatedScenes;
  });
  return newClip;
};
